// src/common/import-result.types.ts
var ImportIssueSeverity = /* @__PURE__ */ ((ImportIssueSeverity2) => {
  ImportIssueSeverity2["ERROR"] = "ERROR";
  ImportIssueSeverity2["WARNING"] = "WARNING";
  ImportIssueSeverity2["INFO"] = "INFO";
  return ImportIssueSeverity2;
})(ImportIssueSeverity || {});
var ImportIssueCode = /* @__PURE__ */ ((ImportIssueCode2) => {
  ImportIssueCode2["INVALID_FORMAT"] = "INVALID_FORMAT";
  ImportIssueCode2["MISSING_REQUIRED_FIELD"] = "MISSING_REQUIRED";
  ImportIssueCode2["DUPLICATE_ROW"] = "DUPLICATE_ROW";
  ImportIssueCode2["UNKNOWN_AIRPORT_CODE"] = "UNKNOWN_AIRPORT";
  ImportIssueCode2["INVALID_DATE_RANGE"] = "INVALID_DATE_RANGE";
  ImportIssueCode2["UNMAPPED_AIRPORT"] = "UNMAPPED_AIRPORT";
  ImportIssueCode2["SUSPICIOUS_DURATION"] = "SUSPICIOUS_DURATION";
  ImportIssueCode2["MISSING_OPTIONAL_FIELD"] = "MISSING_OPTIONAL";
  ImportIssueCode2["INVALID_TIME_SEQUENCE"] = "INVALID_TIME_SEQ";
  ImportIssueCode2["ROW_SKIPPED"] = "ROW_SKIPPED";
  ImportIssueCode2["EXISTING_MATCH"] = "EXISTING_MATCH";
  ImportIssueCode2["NO_CHANGES_DETECTED"] = "NO_CHANGES";
  return ImportIssueCode2;
})(ImportIssueCode || {});

// src/common/batch-operation-type.enum.ts
var BatchOperationType = /* @__PURE__ */ ((BatchOperationType2) => {
  BatchOperationType2["FLIGHT_LIST_IMPORT_PIPELINE"] = "FLIGHT_LIST_IMPORT_PIPELINE";
  BatchOperationType2["FLIGHT_LIST_IMPORT_APM_PIPELINE"] = "FLIGHT_LIST_IMPORT_APM_PIPELINE";
  BatchOperationType2["ROUTE_LIST_IMPORT_PIPELINE"] = "ROUTE_LIST_IMPORT_PIPELINE";
  BatchOperationType2["FLIGHT_STATUS_REFRESH"] = "FLIGHT_STATUS_REFRESH";
  BatchOperationType2["PROCESS_IMPORT"] = "PROCESS_IMPORT";
  BatchOperationType2["CHANGESET_COMMIT"] = "CHANGESET_COMMIT";
  BatchOperationType2["REBUILD_DERIVED_DATA"] = "REBUILD_DERIVED_DATA";
  BatchOperationType2["DETECT_APPLICABLE_CHANGES"] = "DETECT_APPLICABLE_CHANGES";
  BatchOperationType2["CALCULATE_STATUSES"] = "CALCULATE_STATUSES";
  return BatchOperationType2;
})(BatchOperationType || {});
function isFlightListImportPipeline(type) {
  return type.startsWith("FLIGHT_LIST_IMPORT");
}

// src/common/branded-types.ts
import { z } from "zod";
var CountryConfigIdSchema = z.cuid().brand();
var CountryConfigScopeIdSchema = z.cuid().brand();
var CountryConfigAirportIdSchema = z.cuid().brand();
var CountryConfigFirIdSchema = z.cuid().brand();
var PermitConfigIdSchema = z.cuid().brand();
var UserIdSchema = z.cuid().brand();
var ResourceIdSchema = z.cuid().brand();
function createCountryConfigId(id) {
  return id;
}
function createCountryConfigScopeId(id) {
  return id;
}
function createCountryConfigAirportId(id) {
  return id;
}
function createCountryConfigFirId(id) {
  return id;
}
function createPermitConfigId(id) {
  return id;
}
function createUserId(id) {
  return id;
}
function createResourceId(id) {
  return id;
}
function unwrapId(id) {
  return id;
}

// src/common/changeset-resource-type.enum.ts
var ChangesetResourceType = /* @__PURE__ */ ((ChangesetResourceType2) => {
  ChangesetResourceType2["FLIGHT"] = "Flight";
  ChangesetResourceType2["ROUTE_ITEM"] = "RouteItem";
  ChangesetResourceType2["ENTRY_POINT"] = "EntryPoint";
  ChangesetResourceType2["EXIT_POINT"] = "ExitPoint";
  ChangesetResourceType2["ROUTE_OVER_COUNTRY"] = "RouteOverCountry";
  ChangesetResourceType2["AIRCRAFT"] = "Aircraft";
  return ChangesetResourceType2;
})(ChangesetResourceType || {});

// src/common/changeset-type.enum.ts
var ChangesetType = /* @__PURE__ */ ((ChangesetType2) => {
  ChangesetType2["NEW"] = "NEW";
  ChangesetType2["DELETE"] = "DELETE";
  ChangesetType2["CHANGE"] = "CHANGE";
  return ChangesetType2;
})(ChangesetType || {});

// src/common/monitored-fields.ts
var MONITORED_FIELDS = {
  ["Flight" /* FLIGHT */]: ["sta", "std", "callSign"]
};
function hasMonitoredFieldChanges(resourceType, prevData, currentData) {
  const fields = MONITORED_FIELDS[resourceType];
  if (!fields) {
    return false;
  }
  if (!currentData) return true;
  for (const field of fields) {
    const prevValue = prevData[field];
    const currValue = currentData[field];
    if (JSON.stringify(prevValue) !== JSON.stringify(currValue)) {
      return true;
    }
  }
  return false;
}

// src/common/commit-job.ts
var COMMIT_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: "exponential", delay: 5e3 }
};

// src/common/config-schemas/country-config-scope-config.schema.ts
import { z as z18 } from "zod";

// src/common/form-schemas/contact-person.schema.ts
import { z as z4 } from "zod";

// src/common/form-schemas/forms-of-contact.schema.ts
import { z as z2 } from "zod";
var contactInfoSchema = (field) => z2.object({
  [field]: z2.string().optional(),
  isEmergency: z2.boolean().optional()
}).passthrough();
var externalPortalSchema = z2.object({
  url: z2.string().optional(),
  description: z2.string().optional()
}).passthrough();
var formsOfContactSchema = z2.object({
  phoneInfoList: z2.array(contactInfoSchema("phone")).optional(),
  mobilePhoneInfoList: z2.array(contactInfoSchema("mobilePhone")).optional(),
  faxInfoList: z2.array(contactInfoSchema("fax")).optional(),
  emailInfoList: z2.array(contactInfoSchema("email")).optional(),
  sitaInfoList: z2.array(contactInfoSchema("sita")).optional(),
  aftnInfoList: z2.array(contactInfoSchema("aftn")).optional(),
  afsInfoList: z2.array(contactInfoSchema("afs")).optional(),
  telegramInfoList: z2.array(contactInfoSchema("telegram")).optional(),
  whatsappInfoList: z2.array(contactInfoSchema("whatsapp")).optional(),
  skypeInfoList: z2.array(contactInfoSchema("skype")).optional(),
  externalPortalList: z2.array(externalPortalSchema).optional()
}).passthrough();

// src/common/form-schemas/working-hours.schema.ts
import { z as z3 } from "zod";
var workingHoursSchema = z3.object({
  workingHoursFrom: z3.string().optional(),
  workingHoursTo: z3.string().optional(),
  workingDays: z3.array(z3.string()).optional()
}).passthrough();

// src/common/form-schemas/contact-person.schema.ts
var contactPersonSchema = z4.object({
  firstName: z4.string().optional(),
  lastName: z4.string().optional(),
  position: z4.string().optional(),
  department: z4.string().optional(),
  workingHours: workingHoursSchema.optional()
}).merge(formsOfContactSchema).passthrough();

// src/common/form-schemas/mailing-address.schema.ts
import { z as z5 } from "zod";
var mailingAddressSchema = z5.object({
  streetName: z5.string().optional(),
  number: z5.string().optional(),
  poBox: z5.string().optional(),
  city: z5.string().optional(),
  provinceOrRegion: z5.string().optional(),
  country: z5.string().optional(),
  zipCode: z5.string().optional()
}).passthrough();

// src/common/form-schemas/time-length.schema.ts
import { z as z6 } from "zod";
var timeLengthSchema = z6.object({
  value: z6.union([z6.number(), z6.string()]).optional(),
  type: z6.string().optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/bilateral-agreements.schema.ts
import { z as z7 } from "zod";
var bilateralAgreementsSchema = z7.array(z7.string());

// src/common/config-schemas/sub-schemas/custom-document.schema.ts
import { z as z9 } from "zod";

// src/common/config-schemas/sub-schemas/custom-field.schema.ts
import { z as z8 } from "zod";
var customFieldSchema = z8.object({
  name: z8.string().optional(),
  type: z8.string().optional(),
  label: z8.string().optional(),
  description: z8.string().optional(),
  validateAs: z8.string().optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/custom-document.schema.ts
var customDocumentSchema = z9.object({
  name: z9.string().optional(),
  type: z9.string().optional(),
  group: z9.string().optional(),
  label: z9.string().optional(),
  description: z9.string().optional(),
  hasExpirationDate: z9.boolean().optional(),
  fields: z9.array(customFieldSchema).optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/deadline-settings.schema.ts
import { z as z10 } from "zod";
var DeadlineFrom = /* @__PURE__ */ ((DeadlineFrom2) => {
  DeadlineFrom2["FirstFlightForGroup"] = "first-flight-for-group";
  DeadlineFrom2["SeasonStart"] = "season-start";
  return DeadlineFrom2;
})(DeadlineFrom || {});
var DeadlineType = /* @__PURE__ */ ((DeadlineType2) => {
  DeadlineType2["Minutes"] = "minutes";
  DeadlineType2["Hours"] = "hours";
  DeadlineType2["Days"] = "days";
  DeadlineType2["WorkingDays"] = "workingDays";
  DeadlineType2["Months"] = "months";
  DeadlineType2["Years"] = "years";
  return DeadlineType2;
})(DeadlineType || {});
var coerceBoolean = z10.preprocess((val) => {
  if (val === null || val === void 0) return void 0;
  if (typeof val === "string") return val === "true" || val === "yes";
  return val;
}, z10.boolean().optional());
var coerceNumber = z10.preprocess(
  (val) => typeof val === "string" ? Number(val) : val,
  z10.number()
);
var specificDeadlineSettingSchema = z10.object({
  numberOfFlightCountFrom: coerceNumber.optional(),
  numberOfFlightCountTo: coerceNumber.optional(),
  daysCountFrom: coerceNumber.optional(),
  daysCountTo: coerceNumber.optional(),
  deadlineFrom: z10.string().optional(),
  deadlineType: z10.string().optional(),
  deadlineValue: coerceNumber.optional()
}).passthrough();
var submissionDeadlineSettingsSchema = z10.object({
  required: coerceBoolean.optional(),
  specific: coerceBoolean.optional(),
  deadlineFrom: z10.string().optional(),
  deadlineType: z10.string().optional(),
  deadlineValue: coerceNumber.optional(),
  specificSettings: z10.array(specificDeadlineSettingSchema).optional()
}).passthrough();
var correctionDeadlineSettingsSchema = z10.object({
  enabled: coerceBoolean.optional(),
  specific: coerceBoolean.optional(),
  deadlineFrom: z10.string().optional(),
  deadlineType: z10.string().optional(),
  deadlineValue: coerceNumber.optional(),
  specificSettings: z10.array(specificDeadlineSettingSchema).optional()
}).passthrough();
var deadlineFromEnum = z10.nativeEnum(DeadlineFrom);
var deadlineTypeEnum = z10.nativeEnum(DeadlineType);
var strictSpecificDeadlineSettingSchema = z10.object({
  numberOfFlightCountFrom: coerceNumber,
  numberOfFlightCountTo: coerceNumber,
  daysCountFrom: coerceNumber.optional(),
  daysCountTo: coerceNumber.optional(),
  deadlineFrom: deadlineFromEnum,
  deadlineType: deadlineTypeEnum,
  deadlineValue: coerceNumber
});
var strictSubmissionDeadlineSettingsSchema = z10.object({
  required: coerceBoolean.optional(),
  specific: coerceBoolean.optional(),
  deadlineFrom: deadlineFromEnum.optional(),
  deadlineType: deadlineTypeEnum.optional(),
  deadlineValue: coerceNumber.optional(),
  specificSettings: z10.array(strictSpecificDeadlineSettingSchema).optional()
});

// src/common/config-schemas/sub-schemas/department.schema.ts
import { z as z11 } from "zod";
var directContactSchema = z11.object({
  mailingAddress: mailingAddressSchema.optional(),
  formsOfContact: formsOfContactSchema.optional()
}).passthrough();
var departmentSchema = z11.object({
  name: z11.string().optional(),
  contactPerson: z11.array(contactPersonSchema).optional(),
  directContact: directContactSchema.optional(),
  additionalInformation: z11.string().optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/general-settings.schema.ts
import { z as z12 } from "zod";
var generalSettingsSchema = z12.object({
  isNoFlyZone: z12.boolean().optional(),
  requiresAdvanceNotice: z12.boolean().optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/permit-subject.schema.ts
import { z as z13 } from "zod";
var permitSubjectResourceSchema = z13.object({
  required: z13.string().nullable().optional(),
  min: z13.union([z13.number(), z13.string()]).optional(),
  max: z13.union([z13.number(), z13.string()]).optional(),
  tracked: z13.string().nullable().optional()
}).passthrough();
var permitSubjectSchema = z13.object({
  flights: permitSubjectResourceSchema.optional(),
  aircrafts: permitSubjectResourceSchema.optional(),
  entryPoints: permitSubjectResourceSchema.optional(),
  exitPoints: permitSubjectResourceSchema.optional(),
  routeOverCountry: permitSubjectResourceSchema.optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/required-documents.schema.ts
import { z as z14 } from "zod";
var coerceBoolean2 = z14.preprocess((val) => {
  if (val === null || val === void 0) return void 0;
  if (typeof val === "string") return val === "true" || val === "yes";
  return val;
}, z14.boolean().optional());
var originalDocumentsSchema = z14.object({
  required: coerceBoolean2,
  numberOfCopies: z14.union([z14.number(), z14.string()]).optional()
}).passthrough();
var systemDocumentEntrySchema = z14.object({
  required: coerceBoolean2,
  generatedByPtF: coerceBoolean2,
  incidence: z14.array(z14.string()).optional(),
  originalDocuments: originalDocumentsSchema.optional()
}).passthrough();
var requiredDocumentsSchema = z14.object({
  system: z14.record(z14.string(), systemDocumentEntrySchema).optional(),
  permitConfig: z14.array(z14.string()).optional(),
  countryConfig: z14.array(z14.string()).optional()
}).passthrough();
var requiredFieldsSchema = z14.object({
  system: z14.record(z14.string(), z14.array(z14.string())).optional(),
  permitConfig: z14.array(z14.string()).optional(),
  countryConfig: z14.array(z14.string()).optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/sending-settings.schema.ts
import { z as z15 } from "zod";
var sendingMethodEntrySchema = z15.object({
  method: z15.array(z15.string()).optional()
}).passthrough();
var sendingMethodsSchema = z15.object({
  scheduled: z15.array(sendingMethodEntrySchema).optional(),
  nonScheduled: z15.array(sendingMethodEntrySchema).optional(),
  agent: z15.array(sendingMethodEntrySchema).optional()
}).passthrough();
var sendingSettingsSchema = z15.object({
  recipient: z15.string().optional(),
  sendingMethods: sendingMethodsSchema.optional(),
  acceptedLanguages: z15.array(z15.string()).optional(),
  useLocalTime: z15.boolean().optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/template.schema.ts
import { z as z16 } from "zod";
var templateSchema = z16.object({
  html: z16.string().optional(),
  name: z16.string().optional(),
  label: z16.string().optional(),
  description: z16.string().optional(),
  documentFile: z16.record(z16.string(), z16.unknown()).optional()
}).passthrough();

// src/common/config-schemas/sub-schemas/validity-settings.schema.ts
import { z as z17 } from "zod";
var validitySettingsSchema = z17.object({
  type: z17.string().nullable().optional(),
  length: timeLengthSchema.optional(),
  beforeStdSettings: timeLengthSchema.optional(),
  beforeStaSettings: timeLengthSchema.optional(),
  afterStdSettings: timeLengthSchema.optional(),
  afterStaSettings: timeLengthSchema.optional()
}).passthrough();

// src/common/config-schemas/country-config-scope-config.schema.ts
var countryConfigScopeConfigSchema = z18.object({
  /** Civil Aviation Authority name */
  caaName: z18.string().optional(),
  /** Timezone identifier (IANA format, e.g. 'Europe/Warsaw') */
  timezone: z18.string().optional(),
  /** Additional information text */
  additionalInfo: z18.string().optional(),
  /** Working time configuration */
  workTime: workingHoursSchema.optional(),
  /** Free workdays / holidays */
  freeWorkdays: z18.array(
    z18.object({
      date: z18.string().optional(),
      name: z18.string().optional()
    }).passthrough()
  ).optional(),
  /** General settings (no-fly zone, advance notice, etc.) */
  generalSettings: generalSettingsSchema.optional(),
  /** Scheduled flights department */
  scheduledDepartment: departmentSchema.optional(),
  /** Non-scheduled flights department */
  nonScheduledDepartment: departmentSchema.optional(),
  /** Additional departments */
  otherDepartments: z18.array(departmentSchema).optional(),
  /** Custom field definitions */
  fields: z18.array(customFieldSchema).optional(),
  /** Custom document definitions */
  documents: z18.array(customDocumentSchema).optional(),
  /** Required agent IDs */
  requiredAgents: z18.array(z18.string()).optional(),
  /** Recommended agent IDs */
  recommendedAgents: z18.array(z18.string()).optional()
}).passthrough();

// src/common/config-schemas/permit-config-config.schema.ts
import { z as z19 } from "zod";
var permitConfigConfigSchema = z19.object({
  /** Recipient type: 'caa' | 'agent' | 'caa-and-agent' */
  recipient: z19.string().optional(),
  /** Department type: 'scheduled' | 'non-scheduled' */
  department: z19.string().optional(),
  /** Additional information text */
  additionalInfo: z19.string().optional(),
  /** Whether to use local time for deadline calculations */
  useLocalTime: z19.boolean().optional(),
  /** ICAO document requirements */
  icaoDocument: z19.object({ required: z19.boolean().optional() }).passthrough().optional(),
  /** Document-level settings (accepted languages, urgent info) */
  documents: z19.object({
    acceptedLanguages: z19.array(z19.string()).optional(),
    urgentInfoFromPtF: z19.string().optional()
  }).passthrough().optional(),
  /** Accepted languages for permit applications */
  acceptedLanguages: z19.array(z19.string()).optional(),
  /** Sending methods configuration */
  sendingMethods: sendingMethodsSchema.optional(),
  /** Permit subject configuration (flights, aircrafts, entry/exit points) */
  permitSubject: permitSubjectSchema.optional(),
  /** Submission deadline settings */
  submissionDeadlineSettings: submissionDeadlineSettingsSchema.optional(),
  /** Correction deadline settings */
  correctionDeadlineSettings: correctionDeadlineSettingsSchema.optional(),
  /** Validity of permit settings */
  validityOfPermit: validitySettingsSchema.optional(),
  /** Required documents configuration */
  requiredDocuments: requiredDocumentsSchema.optional(),
  /** Required fields configuration */
  requiredFields: requiredFieldsSchema.optional(),
  /** Bilateral agreements (country codes exempt from permit) */
  bilateralAgreements: bilateralAgreementsSchema.optional(),
  /** Name of the form template file from @repo/permit-templates (e.g., 'default') */
  formTemplate: z19.string().optional(),
  /** Document templates */
  templates: z19.array(templateSchema).optional()
}).passthrough();

// src/common/data-completeness-job.ts
var DATA_COMPLETENESS_QUEUE = "data-completeness";
var DATA_COMPLETENESS_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: "exponential", delay: 5e3 }
};

// src/common/email-job.ts
var EMAIL_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: "exponential", delay: 5e3 }
};

// src/common/error-codes.ts
var ApiErrorCode = /* @__PURE__ */ ((ApiErrorCode2) => {
  ApiErrorCode2["INTERNAL_SERVER_ERROR"] = "INTERNAL_SERVER_ERROR";
  ApiErrorCode2["VALIDATION_ERROR"] = "VALIDATION_ERROR";
  ApiErrorCode2["NOT_FOUND"] = "NOT_FOUND";
  ApiErrorCode2["UNAUTHORIZED"] = "UNAUTHORIZED";
  ApiErrorCode2["FORBIDDEN"] = "FORBIDDEN";
  ApiErrorCode2["BAD_REQUEST"] = "BAD_REQUEST";
  ApiErrorCode2["CONFLICT"] = "CONFLICT";
  ApiErrorCode2["COUNTRY_CONFIG_NOT_FOUND"] = "COUNTRY_CONFIG_NOT_FOUND";
  ApiErrorCode2["COUNTRY_CONFIG_ALREADY_PUBLISHED"] = "COUNTRY_CONFIG_ALREADY_PUBLISHED";
  ApiErrorCode2["COUNTRY_CONFIG_CANNOT_PUBLISH_DRAFT"] = "COUNTRY_CONFIG_CANNOT_PUBLISH_DRAFT";
  ApiErrorCode2["COUNTRY_CONFIG_CANNOT_DISCARD_PUBLISHED"] = "COUNTRY_CONFIG_CANNOT_DISCARD_PUBLISHED";
  ApiErrorCode2["COUNTRY_CONFIG_INVALID_STATUS"] = "COUNTRY_CONFIG_INVALID_STATUS";
  ApiErrorCode2["AIRAC_CYCLE_INVALID"] = "AIRAC_CYCLE_INVALID";
  ApiErrorCode2["AIRAC_CYCLE_ALREADY_EXISTS"] = "AIRAC_CYCLE_ALREADY_EXISTS";
  ApiErrorCode2["COUNTRY_SLUG_INVALID"] = "COUNTRY_SLUG_INVALID";
  ApiErrorCode2["COUNTRY_NOT_FOUND"] = "COUNTRY_NOT_FOUND";
  ApiErrorCode2["PERMIT_CONFIG_NOT_FOUND"] = "PERMIT_CONFIG_NOT_FOUND";
  ApiErrorCode2["PERMIT_CONFIG_INVALID"] = "PERMIT_CONFIG_INVALID";
  ApiErrorCode2["DUPLICATE_ENTRY"] = "DUPLICATE_ENTRY";
  ApiErrorCode2["FOREIGN_KEY_VIOLATION"] = "FOREIGN_KEY_VIOLATION";
  ApiErrorCode2["RECORD_NOT_FOUND"] = "RECORD_NOT_FOUND";
  ApiErrorCode2["DATA_INTEGRITY_VIOLATION"] = "DATA_INTEGRITY_VIOLATION";
  return ApiErrorCode2;
})(ApiErrorCode || {});
var ErrorMessages = {
  // Generic HTTP errors
  ["INTERNAL_SERVER_ERROR" /* INTERNAL_SERVER_ERROR */]: "An unexpected error occurred. Please try again later.",
  ["VALIDATION_ERROR" /* VALIDATION_ERROR */]: "Please check your input and correct the errors.",
  ["NOT_FOUND" /* NOT_FOUND */]: "The requested resource was not found.",
  ["UNAUTHORIZED" /* UNAUTHORIZED */]: "You must be logged in to perform this action.",
  ["FORBIDDEN" /* FORBIDDEN */]: "You do not have permission to perform this action.",
  ["BAD_REQUEST" /* BAD_REQUEST */]: "The request was invalid. Please check your input.",
  ["CONFLICT" /* CONFLICT */]: "This operation conflicts with existing data. Please review and try again.",
  // Country Config specific errors
  ["COUNTRY_CONFIG_NOT_FOUND" /* COUNTRY_CONFIG_NOT_FOUND */]: "Country configuration not found. It may have been deleted.",
  ["COUNTRY_CONFIG_ALREADY_PUBLISHED" /* COUNTRY_CONFIG_ALREADY_PUBLISHED */]: "This country configuration is already published.",
  ["COUNTRY_CONFIG_CANNOT_PUBLISH_DRAFT" /* COUNTRY_CONFIG_CANNOT_PUBLISH_DRAFT */]: "Cannot publish: Configuration is still in draft status.",
  ["COUNTRY_CONFIG_CANNOT_DISCARD_PUBLISHED" /* COUNTRY_CONFIG_CANNOT_DISCARD_PUBLISHED */]: "Cannot discard: Configuration is already published.",
  ["COUNTRY_CONFIG_INVALID_STATUS" /* COUNTRY_CONFIG_INVALID_STATUS */]: "Invalid configuration status. Please refresh and try again.",
  // AIRAC cycle errors
  ["AIRAC_CYCLE_INVALID" /* AIRAC_CYCLE_INVALID */]: "Invalid AIRAC cycle format. Expected format: YYNN (e.g., 2401).",
  ["AIRAC_CYCLE_ALREADY_EXISTS" /* AIRAC_CYCLE_ALREADY_EXISTS */]: "A configuration for this AIRAC cycle already exists.",
  // Country errors
  ["COUNTRY_SLUG_INVALID" /* COUNTRY_SLUG_INVALID */]: "Invalid country code. Must be a 2-letter ISO code (e.g., US, GB).",
  ["COUNTRY_NOT_FOUND" /* COUNTRY_NOT_FOUND */]: "Country not found. Please check the country code.",
  // Permit config errors
  ["PERMIT_CONFIG_NOT_FOUND" /* PERMIT_CONFIG_NOT_FOUND */]: "Permit configuration not found. It may have been deleted.",
  ["PERMIT_CONFIG_INVALID" /* PERMIT_CONFIG_INVALID */]: "Invalid permit configuration. Please check all required fields.",
  // Database errors
  ["DUPLICATE_ENTRY" /* DUPLICATE_ENTRY */]: "A record with this information already exists.",
  ["FOREIGN_KEY_VIOLATION" /* FOREIGN_KEY_VIOLATION */]: "Cannot complete operation: Referenced data does not exist.",
  ["RECORD_NOT_FOUND" /* RECORD_NOT_FOUND */]: "The requested record was not found in the database.",
  ["DATA_INTEGRITY_VIOLATION" /* DATA_INTEGRITY_VIOLATION */]: "This operation would violate data integrity rules."
};

// src/common/import-job.ts
var IMPORT_PIPELINE_QUEUE = "import-pipeline";
var IMPORT_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: "exponential", delay: 5e3 }
};

// src/common/import-job-status.enum.ts
var ImportJobStatus = /* @__PURE__ */ ((ImportJobStatus2) => {
  ImportJobStatus2["QUEUED"] = "queued";
  ImportJobStatus2["COMPLETED"] = "completed";
  ImportJobStatus2["FAILED"] = "failed";
  return ImportJobStatus2;
})(ImportJobStatus || {});

// src/common/import-payload-kind.enum.ts
var ImportPayloadKind = /* @__PURE__ */ ((ImportPayloadKind2) => {
  ImportPayloadKind2["FLIGHT_LIST"] = "flight-list";
  ImportPayloadKind2["APM"] = "apm";
  ImportPayloadKind2["ROUTE_LIST"] = "route-list";
  return ImportPayloadKind2;
})(ImportPayloadKind || {});

// src/common/plan-tier.enum.ts
var PlanTier = /* @__PURE__ */ ((PlanTier2) => {
  PlanTier2["FREE"] = "FREE";
  PlanTier2["BASIC"] = "BASIC";
  PlanTier2["PRO"] = "PRO";
  PlanTier2["ENTERPRISE"] = "ENTERPRISE";
  return PlanTier2;
})(PlanTier || {});
function hasAutoGeneration(tier) {
  return tier === "PRO" /* PRO */ || tier === "ENTERPRISE" /* ENTERPRISE */;
}
function requiresManualUpload(tier) {
  return tier === "BASIC" /* BASIC */;
}

// src/common/processing-jobs.ts
var COMMIT_PIPELINE_QUEUE = "commit-pipeline";
var PROCESSING_JOB_OPTIONS = {
  attempts: 3,
  backoff: { type: "exponential", delay: 5e3 }
};

// src/common/processing-lock.ts
var BLOCKING_BATCH_OPERATION_TYPES = [
  "FLIGHT_STATUS_REFRESH" /* FLIGHT_STATUS_REFRESH */,
  "PROCESS_IMPORT" /* PROCESS_IMPORT */,
  "CHANGESET_COMMIT" /* CHANGESET_COMMIT */,
  "REBUILD_DERIVED_DATA" /* REBUILD_DERIVED_DATA */,
  "DETECT_APPLICABLE_CHANGES" /* DETECT_APPLICABLE_CHANGES */,
  "CALCULATE_STATUSES" /* CALCULATE_STATUSES */
];

// src/common/status-enums.ts
var FlightStatus = /* @__PURE__ */ ((FlightStatus2) => {
  FlightStatus2[FlightStatus2["NO_ROUTE_FOUND"] = 0] = "NO_ROUTE_FOUND";
  FlightStatus2[FlightStatus2["NO_COUNTRY_FOUND"] = 1] = "NO_COUNTRY_FOUND";
  FlightStatus2[FlightStatus2["NO_SERVICE_TYPE_FOUND"] = 2] = "NO_SERVICE_TYPE_FOUND";
  FlightStatus2[FlightStatus2["AFTER_DEADLINE"] = 3] = "AFTER_DEADLINE";
  FlightStatus2[FlightStatus2["NOT_APPLIED"] = 4] = "NOT_APPLIED";
  FlightStatus2[FlightStatus2["TO_CANCEL"] = 5] = "TO_CANCEL";
  FlightStatus2[FlightStatus2["PENDING"] = 6] = "PENDING";
  FlightStatus2[FlightStatus2["APPROVED"] = 7] = "APPROVED";
  FlightStatus2[FlightStatus2["OVERFLIGHT_TO_CANCEL"] = 8] = "OVERFLIGHT_TO_CANCEL";
  FlightStatus2[FlightStatus2["CANCELED"] = 9] = "CANCELED";
  FlightStatus2[FlightStatus2["NOT_REQUIRED"] = 10] = "NOT_REQUIRED";
  FlightStatus2[FlightStatus2["TO_DELAYED"] = 11] = "TO_DELAYED";
  FlightStatus2[FlightStatus2["DELAYED"] = 12] = "DELAYED";
  FlightStatus2[FlightStatus2["ARCHIVED"] = 13] = "ARCHIVED";
  FlightStatus2[FlightStatus2["DELETED"] = 14] = "DELETED";
  return FlightStatus2;
})(FlightStatus || {});
var RouteItemStatus = /* @__PURE__ */ ((RouteItemStatus2) => {
  RouteItemStatus2[RouteItemStatus2["ALL_NOT_AVAILABLE"] = 0] = "ALL_NOT_AVAILABLE";
  RouteItemStatus2[RouteItemStatus2["SOME_NOT_AVAILABLE"] = 1] = "SOME_NOT_AVAILABLE";
  RouteItemStatus2[RouteItemStatus2["ALL_AVAILABLE"] = 2] = "ALL_AVAILABLE";
  return RouteItemStatus2;
})(RouteItemStatus || {});
var AircraftStatus = /* @__PURE__ */ ((AircraftStatus2) => {
  AircraftStatus2[AircraftStatus2["ALL_NOT_APPLIED"] = 0] = "ALL_NOT_APPLIED";
  AircraftStatus2[AircraftStatus2["SOME_NOT_APPLIED"] = 1] = "SOME_NOT_APPLIED";
  AircraftStatus2[AircraftStatus2["ALL_WITH_MISSING_OR_EXPIRED_DOCUMENTS"] = 2] = "ALL_WITH_MISSING_OR_EXPIRED_DOCUMENTS";
  AircraftStatus2[AircraftStatus2["SOME_WITH_MISSING_OR_EXPIRED_DOCUMENTS"] = 3] = "SOME_WITH_MISSING_OR_EXPIRED_DOCUMENTS";
  AircraftStatus2[AircraftStatus2["ALL_APPLIED"] = 4] = "ALL_APPLIED";
  return AircraftStatus2;
})(AircraftStatus || {});
var ApplicableChangesetType = /* @__PURE__ */ ((ApplicableChangesetType2) => {
  ApplicableChangesetType2["NEW"] = "NEW";
  ApplicableChangesetType2["DELETE"] = "DELETE";
  ApplicableChangesetType2["CHANGE"] = "CHANGE";
  ApplicableChangesetType2["DELAY"] = "DELAY";
  ApplicableChangesetType2["POSITIONING"] = "POSITIONING";
  return ApplicableChangesetType2;
})(ApplicableChangesetType || {});

// src/common/organization-type.enum.ts
var OrganizationType = /* @__PURE__ */ ((OrganizationType2) => {
  OrganizationType2["AIRLINE"] = "AIRLINE";
  return OrganizationType2;
})(OrganizationType || {});

// src/common/tenant-feature.enum.ts
var TenantFeature = /* @__PURE__ */ ((TenantFeature2) => {
  TenantFeature2["FLIGHTS"] = "flights";
  TenantFeature2["PERMITS"] = "permits";
  TenantFeature2["AIRCRAFT"] = "aircraft";
  TenantFeature2["CREW"] = "crew";
  TenantFeature2["DOCUMENT_AUTO_GENERATION"] = "document-auto-generation";
  TenantFeature2["SELF_MANAGED_DOCUMENTS"] = "self-managed-documents";
  TenantFeature2["ANALYTICS"] = "analytics";
  TenantFeature2["BULK_OPERATIONS"] = "bulk-operations";
  TenantFeature2["API_ACCESS"] = "api-access";
  TenantFeature2["AUDIT_LOG"] = "audit-log";
  TenantFeature2["CUSTOM_ROLES"] = "custom-roles";
  TenantFeature2["SSO"] = "sso";
  return TenantFeature2;
})(TenantFeature || {});
var PLAN_FEATURES = {
  ["FREE" /* FREE */]: [
    "flights" /* FLIGHTS */,
    "permits" /* PERMITS */,
    "aircraft" /* AIRCRAFT */
  ],
  ["BASIC" /* BASIC */]: [
    "flights" /* FLIGHTS */,
    "permits" /* PERMITS */,
    "aircraft" /* AIRCRAFT */,
    "crew" /* CREW */,
    "self-managed-documents" /* SELF_MANAGED_DOCUMENTS */
  ],
  ["PRO" /* PRO */]: [
    "flights" /* FLIGHTS */,
    "permits" /* PERMITS */,
    "aircraft" /* AIRCRAFT */,
    "crew" /* CREW */,
    "self-managed-documents" /* SELF_MANAGED_DOCUMENTS */,
    "document-auto-generation" /* DOCUMENT_AUTO_GENERATION */,
    "analytics" /* ANALYTICS */,
    "bulk-operations" /* BULK_OPERATIONS */
  ],
  ["ENTERPRISE" /* ENTERPRISE */]: [
    "flights" /* FLIGHTS */,
    "permits" /* PERMITS */,
    "aircraft" /* AIRCRAFT */,
    "crew" /* CREW */,
    "self-managed-documents" /* SELF_MANAGED_DOCUMENTS */,
    "document-auto-generation" /* DOCUMENT_AUTO_GENERATION */,
    "analytics" /* ANALYTICS */,
    "bulk-operations" /* BULK_OPERATIONS */,
    "api-access" /* API_ACCESS */,
    "audit-log" /* AUDIT_LOG */,
    "custom-roles" /* CUSTOM_ROLES */,
    "sso" /* SSO */
  ]
};
function getFeaturesForPlan(tier) {
  return PLAN_FEATURES[tier] ?? [];
}
function planHasFeature(tier, feature) {
  return PLAN_FEATURES[tier]?.includes(feature) ?? false;
}
function getMinimumPlanForFeature(feature) {
  const tiers = [
    "FREE" /* FREE */,
    "BASIC" /* BASIC */,
    "PRO" /* PRO */,
    "ENTERPRISE" /* ENTERPRISE */
  ];
  return tiers.find((tier) => PLAN_FEATURES[tier]?.includes(feature));
}

export {
  ImportIssueSeverity,
  ImportIssueCode,
  BatchOperationType,
  isFlightListImportPipeline,
  CountryConfigIdSchema,
  CountryConfigScopeIdSchema,
  CountryConfigAirportIdSchema,
  CountryConfigFirIdSchema,
  PermitConfigIdSchema,
  UserIdSchema,
  ResourceIdSchema,
  createCountryConfigId,
  createCountryConfigScopeId,
  createCountryConfigAirportId,
  createCountryConfigFirId,
  createPermitConfigId,
  createUserId,
  createResourceId,
  unwrapId,
  ChangesetResourceType,
  ChangesetType,
  MONITORED_FIELDS,
  hasMonitoredFieldChanges,
  COMMIT_JOB_OPTIONS,
  formsOfContactSchema,
  workingHoursSchema,
  contactPersonSchema,
  mailingAddressSchema,
  timeLengthSchema,
  bilateralAgreementsSchema,
  customFieldSchema,
  customDocumentSchema,
  DeadlineFrom,
  DeadlineType,
  submissionDeadlineSettingsSchema,
  correctionDeadlineSettingsSchema,
  strictSubmissionDeadlineSettingsSchema,
  departmentSchema,
  generalSettingsSchema,
  permitSubjectSchema,
  systemDocumentEntrySchema,
  requiredDocumentsSchema,
  requiredFieldsSchema,
  sendingMethodsSchema,
  sendingSettingsSchema,
  templateSchema,
  validitySettingsSchema,
  countryConfigScopeConfigSchema,
  permitConfigConfigSchema,
  DATA_COMPLETENESS_QUEUE,
  DATA_COMPLETENESS_JOB_OPTIONS,
  EMAIL_JOB_OPTIONS,
  ApiErrorCode,
  ErrorMessages,
  IMPORT_PIPELINE_QUEUE,
  IMPORT_JOB_OPTIONS,
  ImportJobStatus,
  ImportPayloadKind,
  PlanTier,
  hasAutoGeneration,
  requiresManualUpload,
  COMMIT_PIPELINE_QUEUE,
  PROCESSING_JOB_OPTIONS,
  BLOCKING_BATCH_OPERATION_TYPES,
  FlightStatus,
  RouteItemStatus,
  AircraftStatus,
  ApplicableChangesetType,
  OrganizationType,
  TenantFeature,
  PLAN_FEATURES,
  getFeaturesForPlan,
  planHasFeature,
  getMinimumPlanForFeature
};
